import {CurrencyPipe, NgClass} from '@angular/common';
import {Component} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {EmDatePipe, PhonePipe, TranslationService} from '@emma-labs/shared/angular';
import {getMonthlyPrice, Proposal} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Button} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {InputTextModule} from 'primeng/inputtext';
import {TableModule} from 'primeng/table';
import {ToastModule} from 'primeng/toast';
import {debounceTime, first, of, switchMap} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {ProposalService} from '../../services/proposal/proposal.service';
import {getProducts, ProductType} from '../../utils/product-type';
import {SHORT_DEBOUNCE_TIME} from '../../utils/utility-constants';
import {MainComponent} from '../main/main.component';
import {TableEntry} from './table-entry';

@UntilDestroy()
@Component({
  selector: 'em-client-search-page',
  templateUrl: './client-search-page.component.html',
  imports: [
    CardModule,
    TableModule,
    ReactiveFormsModule,
    PhonePipe,
    NgClass,
    CurrencyPipe,
    Button,
    ToastModule,
    MainComponent,
    InputTextModule,
    EmDatePipe,
  ],
})
export class ClientSearchPageComponent {
  tableEntries: TableEntry[] = [];
  readonly productTypeNg = ProductType;
  readonly filter: FormControl<string | undefined>;

  constructor(
    readonly fb: NonNullableFormBuilder,
    private readonly routerService: Router,
    private readonly proposalService: ProposalService,
    private readonly translationService: TranslationService,
  ) {
    this.filter = fb.control(undefined);

    this.filter.valueChanges
      .pipe(
        debounceTime(SHORT_DEBOUNCE_TIME),
        distinctUntilChanged(),
        switchMap((filter) => {
          if (!filter || filter === '') {
            return of([]);
          }
          return this.proposalService.search(filter, true);
        }),
        untilDestroyed(this),
        map((proposals) => this.mapProposalsToTableEntries(proposals)),
      )
      .subscribe((tableEntries) => (this.tableEntries = tableEntries));
  }

  createProposal(tableEntry: TableEntry) {
    this.proposalService
      .createCopy(tableEntry.proposalId)
      .pipe(first())
      .subscribe((proposal) => {
        this.routerService.navigate(['/', 'dashboard', 'proposal', btoa(proposal.id)]);
      });
  }

  private mapProposalsToTableEntries(proposals: Proposal[]): TableEntry[] {
    return proposals.map((proposal) => ({
      proposalId: proposal.id,
      clientId: proposal.userId,
      proposalCreatedAt: proposal.createdAt,
      fullName: `${proposal.submitter?.firstName ?? ''} ${proposal.submitter?.lastName ?? ''}`,
      phone: proposal.submitter?.phone,
      email: proposal.submitter?.email,
      monthlyPrice: getMonthlyPrice(proposal.protection?.personInsurances ?? []),
      products: getProducts(proposal.protection?.personInsurances ?? []).map((product) => ({
        ...product,
        name:
          product.type !== ProductType.RIDER
            ? product.name
            : (this.translationService.getTranslation(product.name) ?? ''),
      })),
    }));
  }
}
